import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, CalendarDays, ChevronDown, Download, FileText, Loader2, MoreHorizontal, Power, Search,
  Send, UserRound,
} from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { formatBytes } from "../../lib/portalFiles";
import { cellOf, dueLine, waitingFor, whenLine } from "../../lib/portalKinds";
import { Chip } from "../portal/PortalShell";
import SubmissionHistory, { downloadFile } from "../portal/SubmissionHistory";
import AttendanceTable from "../portal/AttendanceTable";
import PresenceBoard from "../portal/PresenceBoard";
import PortfolioBoard from "../portal/PortfolioBoard";
import CompositionBoard from "../portal/CompositionBoard";
import FinalBoard from "../portal/FinalBoard";
import { downloadBlob } from "../../lib/downloadBlob";
import PreviewButton from "./PreviewButton";
import CourseLibrary from "./CourseLibrary";
import ChangeRequests from "./ChangeRequests";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { phaseNote } from "../../lib/groupList";
import {
  BUCKETS, bucketed, bucketOf, groupCode, inviteParticipantsText, openConfirmText, participantsState, period,
  readinessLine, readyNoteLine, shortName, stopConfirmText,
} from "../../lib/portalAdmin";
import { isFped } from "../../lib/regime";
import SimpleAttendance from "../portal/SimpleAttendance";
import ModeChoice from "./assessment/ModeChoice";
import { defaultMode, firstDay, started as startedGroup, startedNote } from "../../lib/initialAssessmentAdmin";
import { MODES as FEEDBACK_MODES, startedNote as feedbackStartedNote } from "../../lib/finalFeedbackAdmin";

/**
 * The team's view of the portal (PR 5). Every group, what is late, any
 * participant's sheet and dossier, the export for the course dossier.
 *
 * Owner and admin only, like the contracts: what participants upload is
 * personal data. Downloads and exports are written to the audit log.
 */
export default function PortalTab() {
  const [view, setView] = useState({ name: "list" });
  if (view.name === "group") {
    return <GroupView groupId={view.groupId} onBack={() => setView({ name: "list" })}
      onOpen={(enrollmentId) => setView({ name: "sheet", groupId: view.groupId, enrollmentId })} />;
  }
  if (view.name === "library") {
    return <CourseLibrary onBack={() => setView({ name: "list" })} />;
  }
  if (view.name === "requests") {
    return <ChangeRequests onBack={() => setView({ name: "list" })} />;
  }
  if (view.name === "sheet") {
    return <SheetView groupId={view.groupId} enrollmentId={view.enrollmentId}
      onBack={() => setView({ name: "group", groupId: view.groupId })} />;
  }
  return <Overview onOpenGroup={(groupId) => setView({ name: "group", groupId })}
    onOpenSheet={(groupId, enrollmentId) => setView({ name: "sheet", groupId, enrollmentId })}
    onOpenLibrary={() => setView({ name: "library" })} onOpenRequests={() => setView({ name: "requests" })} />;
}

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const quietBtn = "inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7]";

const fold = (v) => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function matches(g, q) {
  const term = fold(q).trim();
  if (!term) return true;
  return fold(g.name).includes(term) || fold(g.trainer_name).includes(term) || fold(groupCode(g.name)).includes(term);
}

function Overview({ onOpenGroup, onOpenSheet, onOpenLibrary, onOpenRequests }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [only, setOnly] = useState(null);
  const [shown, setShown] = useState(() => Object.fromEntries(BUCKETS.map((b) => [b.key, b.openByDefault])));
  const [confirm, setConfirm] = useState(null); // {mode: "open" | "stop", group}
  // The initial assessment, asked when the portal opens (15.10.2026): {mode, note}.
  const [assessment, setAssessment] = useState({ mode: "", note: "" });
  const asksAssessment = confirm?.mode === "open" && !isFped(confirm.group);
  // The course library or the trainer's own content (28.10.2026): chosen when the portal opens.
  const [contentFormat, setContentFormat] = useState("library");
  useEffect(() => {
    if (confirm?.mode === "open") setContentFormat(confirm.group.content_format || "own");
  }, [confirm]);
  useEffect(() => {
    if (confirm?.mode !== "open") return;
    const chosen = confirm.group.initial_assessment || {};
    setAssessment({ mode: chosen.mode || defaultMode(confirm.group), note: chosen.note || "" });
  }, [confirm]);
  // The final feedback, asked when the portal opens too (30.10.2026): every group.
  const [feedbackMode, setFeedbackMode] = useState("automat");
  useEffect(() => {
    if (confirm?.mode === "open") setFeedbackMode(confirm.group.final_feedback_mode || "automat");
  }, [confirm]);
  const [busy, setBusy] = useState(false);
  const [zipping, setZipping] = useState(false);

  const inviteNow = async (g) => {
    try {
      const r = await api.post(`/admin/portal/groups/${g.id}/invite`);
      toast.success(r.data.invited ? `Am trimis ${r.data.invited === 1 ? "o invitație" : `${r.data.invited} invitații`}.`
        : "Toți cei cu acces au fost deja invitați sau au intrat.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite invitațiile."));
    }
  };

  // "Trimite invitațiile cursanților" (08.10.2026): the preview first, then the send.
  const [invite, setInvite] = useState(null); // {group, preview}
  const askInvite = async (g) => {
    try {
      const r = await api.get(`/admin/portal/groups/${g.id}/open-participants/preview`);
      setInvite({ group: g, preview: r.data });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut pregăti invitațiile."));
    }
  };
  const sendInvites = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal/groups/${invite.group.id}/open-participants`);
      const n = r.data.invited || 0;
      toast.success(`Grupa e deschisă pentru cursanți. ${n ? `Am trimis ${n === 1 ? "o invitație" : `${n} invitații`}.`
        : "Nu a fost nimeni de invitat acum."}`);
      setInvite(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite invitațiile."));
    } finally {
      setBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      setData((await api.get("/admin/portal/overview")).data);
    } catch (err) {
      setError(apiErrorMessage(err, "Nu am putut încărca portalul."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    const { mode, group } = confirm;
    setBusy(true);
    try {
      // The choice first: once the portal is open, an "Automat" group could be sent to on the next run.
      if (asksAssessment) {
        await api.put(`/admin/groups/${group.id}/initial-assessment`, assessment);
      }
      if (mode === "open") {
        await api.put(`/admin/groups/${group.id}/final-feedback`, { mode: feedbackMode });
      }
      if (mode === "open" && contentFormat !== (group.content_format || "own")) {
        await api.put(`/admin/portal/groups/${group.id}/content-format`, { format: contentFormat });
      }
      await api.put(`/admin/groups/${group.id}`, { portal_enabled: mode === "open" });
      toast.success(mode === "open" ? `Portalul e deschis pentru ${groupCode(group.name) || group.name}.`
        : `Portalul e oprit pentru ${groupCode(group.name) || group.name}.`);
      setConfirm(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut schimba portalul grupei."));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="text-sm text-[#c0483f]">{error}</p>;
  if (!data) return <div className="py-16 text-center text-[#6B7280]"><Loader2 className="mx-auto animate-spin" /></div>;

  const visible = data.groups.filter((g) => matches(g, q) && (!only || bucketOf(g) === only));
  const open = visible.filter((g) => g.portal_enabled);
  const closed = bucketed(visible.filter((g) => !g.portal_enabled));
  const counts = Object.fromEntries(BUCKETS.map((b) => [b.key, data.groups.filter((g) => bucketOf(g) === b.key).length]));

  return (
    <div className="grid gap-5" data-testid="admin-portal">
      <section className={`${card} p-4`}>
        <SearchBox q={q} setQ={setQ} onOpenSheet={onOpenSheet} />
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Filtrează după fază">
          {BUCKETS.map((b) => (
            <button key={b.key} type="button" onClick={() => setOnly(only === b.key ? null : b.key)}
              aria-pressed={only === b.key}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${only === b.key
                ? "bg-[#965EA7] text-white" : "bg-[#F5F6F6] text-[#373A45] hover:bg-[#EDE4F1]"}`}>
              {b.label} <span className="tabular-nums opacity-80">{counts[b.key]}</span>
            </button>
          ))}
        </div>
      </section>

      {data.late_feedback.length > 0 && (
        <section className={`${card} p-5`}>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-[#a33a32]">
            <AlertTriangle size={16} /> Feedback întârziat
          </h2>
          <p className="mt-1 text-xs text-[#6B7280]">Predări care așteaptă de peste 3 zile.</p>
          <ul className="mt-3 divide-y divide-[#E5E7EB]">
            {data.late_feedback.map((t) => (
              <li key={`${t.enrollment_id}-${t.activity_id}`}>
                <button type="button" onClick={() => onOpenSheet(t.group_id, t.enrollment_id)}
                  className="flex w-full items-center gap-3 py-2.5 text-left text-sm hover:text-[#965EA7]">
                  <span className="min-w-0 flex-1 truncate">
                    <span className="font-semibold">{t.name}</span> · {t.title}
                    <span className="text-[#6B7280]"> · {groupCode(t.group_name) || t.group_name} · {t.trainer_name || "fără formator"}</span>
                  </span>
                  <span className="shrink-0 text-xs font-semibold text-[#a33a32]">{waitingFor(t.since)}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={card} data-testid="admin-portal-open-groups">
        <div className="flex items-baseline justify-between gap-3 px-5 py-4">
          <h2 className="text-sm font-semibold text-[#373A45]">Portal deschis</h2>
          <span className="text-xs text-[#6B7280]">{open.length === 1 ? "o grupă" : `${open.length} grupe`}</span>
        </div>
        {open.length === 0 ? (
          <p className="border-t border-[#E5E7EB] px-5 py-4 text-sm text-[#6B7280]">
            {q || only ? "Nicio grupă deschisă nu se potrivește filtrului." : "Nicio grupă încă. Deschide portalul pe o grupă-pilot, mai jos."}
          </p>
        ) : open.map((g) => (
          <div key={g.id} className="grid gap-3 border-t border-[#E5E7EB] px-5 py-3.5 md:grid-cols-[minmax(0,1fr)_auto] md:items-center"
            data-testid={`admin-portal-row-${g.id}`}>
            <GroupIdentity g={g} badge={<Chip tone={participantsState(g).tone}>{participantsState(g).label}</Chip>} />
            <div className="flex flex-wrap items-center gap-4">
              <dl className="grid grid-cols-4 gap-4 text-left">
                <Kpi label="Au intrat" value={g.entered} of={g.signed} />
                <Kpi label="Teme" value={g.stats.assignments} />
                <Kpi label="Predări" value={g.stats.handed_in} />
                <Kpi label="Așteaptă" value={g.stats.waiting} late={g.stats.late} />
              </dl>
              <div className="flex items-center gap-1">
                {!g.portal_participants_opened_at && (
                  <button type="button" onClick={() => askInvite(g)} data-testid="admin-portal-invite-participants"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986]">
                    <Send size={13} /> Trimite invitațiile cursanților
                  </button>
                )}
                <button type="button" onClick={() => onOpenGroup(g.id)} data-testid="admin-portal-group"
                  className="rounded-full border border-[#E5E7EB] px-3.5 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7]">
                  Deschide
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label={`Mai multe pentru ${g.name}`}
                      className="rounded-full p-2 text-[#6B7280] hover:bg-[#F5F6F6] hover:text-[#373A45]">
                      <MoreHorizontal size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {g.portal_participants_opened_at && (
                      <DropdownMenuItem onSelect={() => inviteNow(g)}>
                        <Send size={14} className="mr-2" /> Trimite acum invitațiile noilor semnatari
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem disabled={zipping} onSelect={() => exportZip(g.id, g.name, setZipping)}>
                      <Download size={14} className="mr-2" /> Exportă pentru dosar (ZIP)
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-[#c0483f]" onSelect={() => setConfirm({ mode: "stop", group: g })}>
                      <Power size={14} className="mr-2" /> Oprește portalul
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            <p className="text-[11px] text-[#6B7280] md:col-span-2">
              {g.portal_participants_opened_at
                ? `Cursanți invitați din ${whenLine(g.portal_participants_opened_at)} · invitații trimise: ${g.invited || 0}. `
                  + "Cine semnează de acum primește invitația automat."
                : g.portal_trainer_ready_at
                  ? `Formatorul a anunțat că grupa e pregătită (${whenLine(g.portal_trainer_ready_at)})`
                    + (g.portal_trainer_ready_note ? `: „${g.portal_trainer_ready_note}”` : ".")
                  : "Doar formatorul are acces și pregătește grupa. Cursanții primesc invitația când o trimiți tu."}
              {g.last_export_at && ` · Ultimul export pentru dosar: ${whenLine(g.last_export_at)}`}
            </p>
          </div>
        ))}
      </section>

      <section className={card} data-testid="admin-portal-closed-groups">
        <div className="px-5 py-4">
          <h2 className="text-sm font-semibold text-[#373A45]">Celelalte grupe</h2>
          <p className="mt-0.5 text-xs text-[#6B7280]">Cât timp portalul e oprit, nimeni din grupă nu intră.</p>
        </div>
        {BUCKETS.map((b) => {
          const rows = closed[b.key];
          if (!rows.length) return null;
          const expanded = shown[b.key] || Boolean(only) || Boolean(q);
          return (
            <div key={b.key}>
              <button type="button" onClick={() => setShown({ ...shown, [b.key]: !shown[b.key] })}
                aria-expanded={expanded}
                className="flex w-full items-center gap-2 border-t border-[#E5E7EB] bg-[#F9FAFB] px-5 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] hover:text-[#373A45]">
                <ChevronDown size={14} className={`transition-transform ${expanded ? "" : "-rotate-90"}`} />
                {b.label} <span className="tabular-nums">· {rows.length}</span>
              </button>
              {expanded && rows.map((g) => (
                <div key={g.id} className="grid gap-2 border-t border-[#E5E7EB] px-5 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <GroupIdentity g={g} extra={readinessLine(g)} />
                  {/* Finished cohorts keep the button: the portfolio is due 10 days after the last session. */}
                  {b.key === "anulate" ? null : g.trainer_name ? (
                    <button type="button" onClick={() => setConfirm({ mode: "open", group: g })} data-testid="admin-portal-open"
                      className="justify-self-start rounded-full border border-[#E5E7EB] px-3.5 py-1.5 text-xs font-semibold text-[#373A45] hover:border-[#965EA7] hover:text-[#965EA7] sm:justify-self-end">
                      Deschide portalul
                    </button>
                  ) : (
                    <button type="button" onClick={() => navigate("/admin/groups")}
                      className="justify-self-start text-xs font-semibold text-[#8a5d12] hover:underline sm:justify-self-end"
                      title="Fără formator, portalul n-ar avea cine să dea teme">
                      Alege întâi formatorul
                    </button>
                  )}
                </div>
              ))}
            </div>
          );
        })}
        {BUCKETS.every((b) => closed[b.key].length === 0) && (
          <p className="border-t border-[#E5E7EB] px-5 py-4 text-sm text-[#6B7280]">Nicio grupă nu se potrivește filtrului.</p>
        )}
      </section>

      <section className={`${card} flex flex-wrap items-center justify-between gap-3 p-5`} data-testid="admin-portal-library-card">
        <div>
          <h3 className="font-semibold text-[#373A45]">Biblioteca cursului</h3>
          <p className="text-sm text-[#6B7280]">Prezentările, suportul de curs și activitățile din sesiunile live, pe lecții, puse o singură dată.</p>
        </div>
        <button type="button" onClick={onOpenLibrary} className={quietBtn} data-testid="admin-portal-library-open">
          Deschide biblioteca
        </button>
      </section>
      <RequestsCard onOpen={onOpenRequests} />

      <HoursSettings />
      <PortfolioSettings />
      <AnnexProgramSettings />
      <AnnexSignatureSettings />

      <Dialog open={Boolean(invite)} onOpenChange={(v) => { if (!v && !busy) setInvite(null); }}>
        <DialogContent className="max-w-md">
          {invite && (
            <>
              <DialogHeader>
                <DialogTitle>Trimiți invitațiile cursanților pentru {groupCode(invite.group.name) || invite.group.name}?</DialogTitle>
                <DialogDescription asChild>
                  <div className="grid gap-2 text-sm text-[#373A45]" data-testid="admin-portal-invite-preview">
                    {!invite.preview.trainer_ready_at && (
                      <p className="rounded-lg bg-[#d99a2b]/15 px-3 py-2 font-semibold text-[#8a5d12]">
                        Formatorul nu a anunțat încă echipa că grupa e pregătită. Trimiți totuși?
                      </p>
                    )}
                    {invite.preview.trainer_ready_at && (
                      <p className="rounded-lg bg-[#519872]/10 px-3 py-2 text-[#3c7457]" data-testid="admin-portal-invite-ready">
                        {readyNoteLine(invite.preview)}
                      </p>
                    )}
                    {inviteParticipantsText(invite.preview).map((line) => <p key={line}>{line}</p>)}
                    <p className="text-xs text-[#6B7280]">Din acest moment, cursanții intră în portal și primesc emailurile obișnuite.</p>
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <button type="button" disabled={busy} onClick={() => setInvite(null)}
                  className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45]">Renunță</button>
                <button type="button" disabled={busy} onClick={sendInvites} data-testid="admin-portal-invite-confirm"
                  className="rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986]">
                  {busy && <Loader2 size={14} className="mr-1.5 inline animate-spin" />}
                  Trimite invitațiile
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirm)} onOpenChange={(v) => { if (!v && !busy) setConfirm(null); }}>
        <DialogContent className="max-w-md">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {confirm.mode === "open" ? "Deschizi portalul pentru " : "Oprești portalul pentru "}
                  {groupCode(confirm.group.name) || confirm.group.name}?
                </DialogTitle>
                <DialogDescription>
                  {confirm.mode === "open" ? openConfirmText(confirm.group) : stopConfirmText(confirm.group)}
                </DialogDescription>
              </DialogHeader>
              {confirm.mode === "open" && (
                <fieldset className="grid gap-2" data-testid="admin-portal-content-format">
                  <legend className="text-sm font-semibold text-[#373A45]">Conținutul grupei</legend>
                  {[
                    ["library", "Biblioteca cursului",
                      "Grupa primește singură prezentările, suportul de curs și temele, lecție cu lecție, la începutul sesiunii lecției."],
                    ["own", "Conținut propriu",
                      "Ca până acum: formatorul pune singur materialele și temele."],
                  ].map(([value, label, hint]) => (
                    <label key={value} className="flex items-start gap-2 text-sm">
                      <input type="radio" name="content-format" className="mt-1 accent-[#965EA7]" disabled={busy}
                        checked={contentFormat === value} onChange={() => setContentFormat(value)} />
                      <span><strong className="text-[#373A45]">{label}</strong>
                        <span className="block text-xs text-[#6B7280]">{hint}</span></span>
                    </label>
                  ))}
                </fieldset>
              )}
              {asksAssessment && (
                <div className="grid gap-2" data-testid="admin-portal-assessment">
                  <div className="text-sm font-semibold text-[#373A45]">Evaluarea inițială pentru această grupă</div>
                  <ModeChoice value={assessment.mode} note={assessment.note} disabled={busy}
                    onChange={(m) => setAssessment((a) => ({ ...a, mode: m }))}
                    onNote={(n) => setAssessment((a) => ({ ...a, note: n }))} />
                  {startedGroup(confirm.group) && (
                    <p className="text-xs text-[#8a6a1a]" data-testid="admin-portal-assessment-started">
                      {startedNote(firstDay(confirm.group))}
                    </p>
                  )}
                </div>
              )}
              {confirm.mode === "open" && (
                <fieldset className="grid gap-2" data-testid="admin-portal-feedback">
                  <legend className="text-sm font-semibold text-[#373A45]">Feedbackul de final pentru această grupă</legend>
                  {FEEDBACK_MODES.map((m) => (
                    <label key={m.key} className="flex items-start gap-2 text-sm">
                      <input type="radio" name="feedback-mode" className="mt-1 accent-[#965EA7]" disabled={busy}
                        checked={feedbackMode === m.key} onChange={() => setFeedbackMode(m.key)} />
                      <span><strong className="text-[#373A45]">{m.label}:</strong> <span className="text-[#6B7280]">{m.hint}</span></span>
                    </label>
                  ))}
                  {startedGroup(confirm.group) && (
                    <p className="text-xs text-[#8a6a1a]">{feedbackStartedNote(firstDay(confirm.group))}</p>
                  )}
                </fieldset>
              )}
              <DialogFooter>
                <button type="button" disabled={busy} onClick={() => setConfirm(null)}
                  className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45]">Renunță</button>
                <button type="button" disabled={busy} onClick={apply} data-testid="admin-portal-confirm"
                  className={`rounded-full px-4 py-2 text-sm font-semibold text-white ${confirm.mode === "open"
                    ? "bg-[#965EA7] hover:bg-[#7A4986]" : "bg-[#c0483f] hover:bg-[#a33a32]"}`}>
                  {busy && <Loader2 size={14} className="mr-1.5 inline animate-spin" />}
                  {confirm.mode === "open" ? "Deschide portalul" : "Oprește portalul"}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** A running group onto the course library (28.10.2026, plan PR 6): only the
 *  lessons that have not begun; what the trainer put in stays. */
function MoveToLibrary({ groupId, name }) {
  const [busy, setBusy] = useState(false);
  const move = async () => {
    if (!window.confirm(`Treci grupa ${groupCode(name) || name} pe biblioteca cursului? Primește lecțiile care n-au început încă; `
      + "temele și materialele puse de formator rămân. Nu se mai poate reveni la conținut propriu.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal/groups/${groupId}/apply-library`);
      toast.success(`Grupa e pe biblioteca cursului: ${r.data.added} ${r.data.added === 1 ? "material nou" : "materiale noi"}`
        + (r.data.skipped ? `, ${r.data.skipped} din lecții deja începute rămase deoparte.` : "."));
      window.location.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trece grupa pe bibliotecă."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={move} disabled={busy} data-testid="admin-portal-move-to-library"
      className="mt-1 text-xs font-semibold text-[#965EA7] hover:underline disabled:opacity-50">
      {busy ? "Se mută…" : "Trece pe biblioteca cursului"}
    </button>
  );
}

/** "Cererile formatorilor" (28.10.2026): how many wait for the team. */
function RequestsCard({ onOpen }) {
  const [pending, setPending] = useState(null);
  useEffect(() => {
    api.get("/admin/portal/change-requests").then((r) => setPending(r.data.pending)).catch(() => setPending(null));
  }, []);
  return (
    <section className={`${card} flex flex-wrap items-center justify-between gap-3 p-5`} data-testid="admin-portal-requests-card">
      <div>
        <h3 className="flex items-center gap-2 font-semibold text-[#373A45]">
          Cererile formatorilor
          {pending > 0 && <span className="rounded-full bg-[#d99a2b]/15 px-2 py-0.5 text-xs font-semibold text-[#8a5d12]">{pending} în așteptare</span>}
        </h3>
        <p className="text-sm text-[#6B7280]">Modificări cerute la temele din bibliotecă, pentru o grupă anume.</p>
      </div>
      <button type="button" onClick={onOpen} className={quietBtn} data-testid="admin-portal-requests-open">Vezi cererile</button>
    </section>
  );
}

/** The hours the accreditation requires and the threshold — settings, because the procedure can change. */
/** How many days after the last session the portfolio closes, by default:
 *  3 to 7 — the structure check shares the same 10 days (PR 9). */
function PortfolioSettings() {
  const [days, setDays] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/portal/portfolio-settings").then((r) => setDays(String(r.data.portfolio_days))).catch(() => {});
  }, []);
  if (days === null) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/portal/portfolio-settings", { portfolio_days: Number(days) });
      setDays(String(r.data.portfolio_days));
      toast.success("Am salvat. Grupele fără un termen propriu îl folosesc de acum.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className={`${card} p-5`} data-testid="admin-portal-portfolio-settings">
      <h2 className="text-sm font-semibold text-[#373A45]">Termenul portofoliului</h2>
      <p className="mt-0.5 text-xs text-[#6B7280]">
        Câte zile după ultima zi de formare se închide depunerea, implicit. Între 3 și 7: verificarea structurii se
        termină tot în ziua 10. Pe o grupă se poate schimba din fișa ei, până la 10.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
          Zile după ultima zi de formare
          <input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)}
            className="w-20 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm tabular-nums focus:border-[#965EA7] focus:outline-none" />
        </label>
        <button type="button" disabled={saving} onClick={save} className={quietBtn}>Salvează</button>
      </div>
    </section>
  );
}

const PROGRAM_LABELS = [
  ["provider", "Denumire furnizor (1)"], ["program", "Denumire program (2)"], ["category", "Categorie program (2)"],
  ["domain", "Domeniu tematic (2)"], ["credits", "Nr. credite (6)"],
  ["modifications", "Nr. aprobare modificări (7)"], ["form", "Forma de organizare (8)"],
  ["target_group", "Grup țintă (10)"], ["training_place", "Locația formării (13)"],
  ["competence_type", "Tip de competențe (Anexa 7)"],
  ["legal_rep_name", "Reprezentant legal — nume și prenume"], ["legal_rep_function", "Reprezentant legal — funcția"],
  ["legal_rep_basis", "Actul de numire a reprezentantului legal (Anexa 7)"],
  ["program_responsible", "Responsabil program formare"],
];

/** The programme's half of the annexes' header: the same for every group. The
 *  accreditation (3–5) and the hours (9) are filled from their own settings. */
function AnnexProgramSettings() {
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/portal/annex-program").then((r) => setForm(r.data.program)).catch(() => {});
  }, []);
  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try {
      setForm((await api.put("/admin/portal/annex-program", form)).data.program);
      toast.success("Am salvat antetul anexelor. Se aplică documentelor generate de acum înainte.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className={`${card} p-5`} data-testid="admin-portal-annex-program">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#373A45]">Antetul anexelor</h2>
          <p className="mt-0.5 text-xs text-[#6B7280]">
            Câmpurile despre program, aceleași pentru toate grupele.
            {!form.domain && " Lipsește domeniul tematic."}
          </p>
        </div>
        <button type="button" className={quietBtn} onClick={() => setOpen((v) => !v)}>{open ? "Ascunde" : "Editează"}</button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {PROGRAM_LABELS.map(([key, label]) => (
            <label key={key} className={`grid gap-1 text-xs font-semibold text-[#6B7280] ${key === "program" ? "sm:col-span-2" : ""}`}>
              {label}
              <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm font-normal text-[#373A45] focus:border-[#965EA7] focus:outline-none" />
            </label>
          ))}
          <div className="sm:col-span-2">
            <button type="button" disabled={saving} onClick={save} className={quietBtn}>Salvează antetul</button>
            <span className="ml-2 text-xs text-[#6B7280]">Acreditarea (3–5) vine din „Calendarul anului școlar”, orele (9) din curriculum.</span>
          </div>
        </div>
      )}
    </section>
  );
}

/** The legal representative's signature with the stamp, put on the annexes'
 *  PDF (03.11.2026). The owner's alone: for anyone else the server says no and
 *  the card stays hidden. */
function AnnexSignatureSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    api.get("/admin/portal/annex-signature").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const { settings, image } = data;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      setData((await fn()).data);
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setBusy(false);
    }
  };
  const upload = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    run(() => api.post("/admin/portal/annex-signature/image", form), image ? "Am înlocuit semnătura." : "Am încărcat semnătura.");
  };
  const toggle = (key) => run(() => api.put("/admin/portal/annex-signature/settings", { [key]: !settings[key] }));
  const remove = () => {
    if (!window.confirm("Ștergi imaginea cu semnătura? Anexele generate de acum înainte ies nesemnate; cele deja generate rămân cum sunt.")) return;
    run(() => api.delete("/admin/portal/annex-signature/image"), "Am șters semnătura.");
  };
  const preview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/portal/annex-signature/preview.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Nu am putut genera previzualizarea.");
    } finally {
      setBusy(false);
    }
  };
  const box = (key, label, hint) => (
    <label className="flex items-start gap-2 text-sm text-[#373A45]">
      <input type="checkbox" checked={Boolean(settings[key])} disabled={busy} onChange={() => toggle(key)}
        className="mt-0.5 accent-[#965EA7]" data-testid={`annex-signature-${key}`} />
      <span>{label}{hint && <span className="block text-xs text-[#6B7280]">{hint}</span>}</span>
    </label>
  );

  return (
    <section className={`${card} p-5`} data-testid="admin-portal-annex-signature">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#373A45]">Semnătura pe anexe</h2>
          <p className="mt-0.5 max-w-2xl text-xs text-[#6B7280]">
            Semnătura cu ștampila reprezentantului legal, pusă pe PDF-ul anexelor 4.2, 4.3, 5, 6, 7, 9, 10.2, 12 și 13.
            Excelul rămâne nesemnat. Anexele deja generate nu se schimbă: pentru o variantă semnată, generează o versiune nouă.
          </p>
        </div>
        <Chip tone={settings.enabled && image ? "good" : "neutral"}>
          {settings.enabled && image ? "Pornită" : "Oprită"}
        </Chip>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        {image ? (
          <span className="text-[#373A45]" data-testid="annex-signature-image">
            Imagine încărcată pe {new Date(image.uploaded_at).toLocaleDateString("ro-RO")} de {image.uploaded_by}
          </span>
        ) : (
          <span className="text-[#6B7280]">Nicio imagine încărcată.</span>
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className={quietBtn}
          data-testid="annex-signature-upload">
          {image ? "Înlocuiește imaginea" : "Încarcă imaginea"}
        </button>
        {image && (
          <>
            <button type="button" disabled={busy} onClick={preview} className={quietBtn} data-testid="annex-signature-preview">
              Previzualizează
            </button>
            <button type="button" disabled={busy} onClick={remove}
              className="text-xs font-semibold text-[#a33a32] hover:underline">Șterge imaginea</button>
          </>
        )}
      </div>
      <p className="mt-1 text-xs text-[#6B7280]">PNG sau JPG, cel mult 5 MB. PNG-ul cu fundal transparent arată cel mai bine.</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {box("enabled", "Pune semnătura pe anexe", image ? null : "Se aplică după ce încarci imaginea.")}
        {box("team", "Echipa o poate pune", "Owner și admin FPED, din panoul de admin.")}
        {box("trainers", "Formatorii o pot pune", "Pe anexele generate din portal: 4.2, 4.3, 5 și 6.")}
      </div>
      <p className="mt-3 text-xs text-[#6B7280]">
        Cine nu are dreptul generează anexa fără semnătură: rândul reprezentantului legal rămâne de semnat de mână.
        Numele reprezentantului legal și al responsabilului de program vin din „Antetul anexelor”.
      </p>
    </section>
  );
}

function HoursSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/portal/settings").then((r) => setForm({
      required_hours: String(r.data.required_hours), threshold: String(r.data.threshold) })).catch(() => {});
  }, []);

  if (!form) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/portal/settings", {
        required_hours: Number(String(form.required_hours).replace(",", ".")),
        threshold: Number(String(form.threshold).replace(",", ".")),
      });
      setForm({ required_hours: String(r.data.required_hours), threshold: String(r.data.threshold) });
      toast.success("Am salvat. Rapoartele de ore se calculează de acum așa.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva."));
    } finally {
      setSaving(false);
    }
  };
  const field = "w-20 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-sm tabular-nums focus:border-[#965EA7] focus:outline-none";
  return (
    <section className={`${card} p-5`} data-testid="admin-portal-hours-settings">
      <h2 className="text-sm font-semibold text-[#373A45]">Orele asincrone</h2>
      <p className="mt-0.5 text-xs text-[#6B7280]">Ce cere acreditarea. Procentele din rapoarte se calculează după aceste două valori.</p>
      <div className="mt-3 flex flex-wrap items-end gap-4 text-sm">
        <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
          Ore asincrone cerute
          <input inputMode="decimal" value={form.required_hours} className={field}
            onChange={(e) => setForm({ ...form, required_hours: e.target.value })} />
        </label>
        <label className="grid gap-1 text-xs font-semibold text-[#6B7280]">
          Prag de prezență (%)
          <input inputMode="decimal" value={form.threshold} className={field}
            onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
        </label>
        <button type="button" disabled={saving} onClick={save} className={quietBtn}>Salvează</button>
      </div>
    </section>
  );
}

function GroupIdentity({ g, extra, badge }) {
  const code = groupCode(g.name);
  const cancelled = bucketOf(g) === "anulate";
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        {code && (
          <span className="rounded-md border border-[#E5E7EB] bg-[#F5F6F6] px-1.5 py-0.5 font-mono text-[11px] font-semibold text-[#373A45]">
            {code}
          </span>
        )}
        <span className="text-sm font-semibold text-[#373A45]" title={g.name}>{shortName(g.name)}</span>
        {isFped(g) && <Chip tone="info">Curs FPED</Chip>}
        <Chip tone={cancelled ? "late" : bucketOf(g) === "in_curs" ? "good" : "neutral"}>
          {cancelled ? "anulată" : phaseNote(g)}
        </Chip>
        {badge}
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-[#6B7280]">
        <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {period(g)}</span>
        <span>·</span>
        {g.trainer_name
          ? <span className="inline-flex items-center gap-1"><UserRound size={12} /> {g.trainer_name}</span>
          : <span className="inline-flex items-center gap-1 text-[#8a5d12]"><AlertTriangle size={12} /> fără formator</span>}
        {extra && <><span>·</span><span className="text-[#373A45]">{extra}</span></>}
      </p>
    </div>
  );
}

function Kpi({ label, value, of, late }) {
  return (
    <div className="min-w-[3.5rem]">
      <dt className="text-[11px] text-[#6B7280]">{label}</dt>
      <dd className="text-base font-semibold tabular-nums text-[#373A45]">
        {value}
        {of !== undefined && <span className="text-xs font-normal text-[#9CA3AF]"> / {of}</span>}
        {late > 0 && <span className="ml-1 text-xs font-semibold text-[#a33a32]">{late} întârz.</span>}
      </dd>
    </div>
  );
}

function SearchBox({ q, setQ, onOpenSheet }) {
  const [res, setRes] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setRes(null); return undefined; }
    timer.current = setTimeout(async () => {
      try {
        setRes((await api.get("/admin/portal/search", { params: { q } })).data);
      } catch {
        setRes({ participants: [], files: [] });
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const found = res && (res.participants.length > 0 || res.files.length > 0);
  return (
    <div>
      <label className="relative block">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută o grupă (S1G2), un cursant sau un fișier"
          className="w-full rounded-xl border border-[#E5E7EB] py-2.5 pl-9 pr-3 text-sm focus:border-[#965EA7] focus:outline-none focus:ring-2 focus:ring-[#965EA7]/20"
          data-testid="admin-portal-search" />
      </label>
      {found && (
        <div className="mt-3 grid gap-2 rounded-xl bg-[#F9FAFB] p-3 text-sm">
          {res.participants.map((p) => (
            <button key={p.enrollment_id} type="button" onClick={() => onOpenSheet(p.group_id, p.enrollment_id)}
              className="text-left hover:text-[#965EA7]">
              <span className="font-semibold">{p.name}</span>
              <span className="text-[#6B7280]"> · {p.email} · {groupCode(p.group_name) || p.group_name}</span>
            </button>
          ))}
          {res.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2">
              <FileText size={14} className="shrink-0 text-[#6B7280]" />
              <button type="button" onClick={() => downloadFile(f.id, (m) => toast.error(m), "/admin/portal")}
                className="min-w-0 truncate text-left hover:text-[#965EA7]">{f.name}</button>
              <span className="shrink-0 text-xs text-[#6B7280]">{f.owner_name} · {groupCode(f.group_name) || f.group_name} · {formatBytes(f.size)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const asciiName = (name) => String(name || "grupa").normalize("NFKD").replace(/[^\w.-]+/g, "_");

function exportZip(groupId, name, setBusy) {
  return downloadBlob(`/admin/portal/groups/${groupId}/export.zip`, `portal-${asciiName(name)}.zip`, setBusy,
    "Exportul e descărcat și trecut în evidență.");
}

function GroupView({ groupId, onBack, onOpen }) {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(null);
  const [error, setError] = useState("");
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    api.get(`/admin/portal/groups/${groupId}`).then((r) => {
      setData(r.data);
      // The asynchronous hours belong to the accredited programme: a Curs FPED
      // cohort has none, so nothing is asked for it.
      if (isFped(r.data.group)) { setHours(null); return; }
      api.get(`/admin/portal/groups/${groupId}/attendance`).then((h) => setHours(h.data)).catch(() => setHours(null));
    }).catch((err) => setError(apiErrorMessage(err, "Nu am găsit grupa.")));
  }, [groupId]);

  return (
    <div className="grid gap-6">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Toate grupele
      </button>
      {error && <p className="text-sm text-[#c0483f]">{error}</p>}
      {!data && !error && <Loader2 className="mx-auto animate-spin text-[#6B7280]" />}
      {data && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              {groupCode(data.group.name) && (
                <p className="font-mono text-xs font-semibold text-[#6B7280]">{groupCode(data.group.name)}</p>
              )}
              <h2 className="font-display text-2xl font-bold text-[#373A45]" title={data.group.name}>{shortName(data.group.name)}</h2>
              <p className="text-sm text-[#6B7280]">
                {period(data.group)} · {data.group.trainer_name || "fără formator"} · {data.participants.length} cursanți în portal
                {" · "}<span data-testid="admin-portal-format">
                  {data.group.content_format === "library" ? "Biblioteca cursului" : "Conținut propriu"}
                </span>
              </p>
              {data.group.portal_enabled && data.group.content_format !== "library" && (
                <MoveToLibrary groupId={groupId} name={data.group.name} />
              )}
              {isFped(data.group) && (
                <p className="mt-1 text-xs text-[#6B7280]" data-testid="admin-portal-fped">
                  <span className="mr-1.5 inline-flex rounded-full bg-[#3F8EBA]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2f6d90]">Curs FPED</span>
                  Teme, materiale, feedback, prezență simplă și dosarul cursantului: fără portofoliu, comisie și anexe.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {data.group.trainer_id && (
              <PreviewButton target={{ trainerId: data.group.trainer_id }} label="Vezi portalul ca formatorul"
                testid="admin-portal-preview-trainer" />
            )}
            <button type="button" disabled={zipping} onClick={() => exportZip(groupId, data.group.name, setZipping)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60"
              data-testid="admin-portal-export">
              {zipping ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Exportă pentru dosar (ZIP)
            </button>
            </div>
          </div>
          <p className="-mt-3 text-xs text-[#6B7280]">
            Versiunile predate și feedbackul, cu un index și amprentele fișierelor. Fără ciorne și fără dosarele personale.
          </p>

          {/* The participants come first (22.10.2026): it is what the group is
              opened for; the hours, presence and annexes follow. */}
          <section className={`${card} overflow-x-auto`} data-testid="admin-portal-participants">
            <h3 className="px-5 pt-4 text-sm font-semibold text-[#373A45]">Cursanții și temele</h3>
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left">
                  <th className="px-5 py-3 text-xs font-semibold text-[#6B7280]">Cursant</th>
                  {data.activities.map((a) => (
                    <th key={a.id} className="px-3 py-3 text-xs font-semibold text-[#373A45]">
                      <span className="block max-w-[11rem] truncate" title={a.title}>{a.title}</span>
                      <span className="block font-normal text-[#6B7280]">{dueLine(a.due_at)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {data.participants.map((p) => (
                  <tr key={p.enrollment_id}>
                    <th className="whitespace-nowrap px-5 py-2.5 text-left font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        <button type="button" onClick={() => onOpen(p.enrollment_id)} className="hover:text-[#965EA7]">{p.name}</button>
                        <PreviewButton target={{ enrollmentId: p.enrollment_id }} label={`Vezi portalul ca ${p.name}`} iconOnly
                          testid={`admin-portal-preview-${p.enrollment_id}`} />
                      </span>
                    </th>
                    {data.activities.map((a) => {
                      const c = p.cells[a.id];
                      const look = cellOf(c);
                      return <td key={a.id} className="px-3 py-2.5"><Chip tone={c?.late_feedback ? "late" : look.tone}>{look.label}</Chip></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.participants.length === 0 && <p className="px-5 py-4 text-sm text-[#6B7280]">Niciun cursant în portal încă.</p>}
          </section>

          {hours && (
            <section className={`${card} p-5`} data-testid="admin-portal-hours">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-[#373A45]">Orele asincrone</h3>
                <div className="flex gap-2">
                  <button type="button" className={quietBtn}
                    onClick={() => downloadBlob(`/admin/portal/groups/${groupId}/attendance.xlsx`,
                      `ore-asincrone-${asciiName(data.group.name)}.xlsx`)}>
                    <Download size={13} /> Excel
                  </button>
                  <button type="button" className={quietBtn}
                    onClick={() => downloadBlob(`/admin/portal/groups/${groupId}/attendance.pdf`,
                      `ore-asincrone-${asciiName(data.group.name)}.pdf`)}>
                    <Download size={13} /> PDF grupă
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <AttendanceTable report={hours} onOpen={onOpen}
                  actions={(p) => (
                    <button type="button" className="text-xs font-semibold text-[#965EA7] hover:underline"
                      onClick={() => downloadBlob(`/admin/portal/groups/${groupId}/attendance.pdf?enrollment_id=${encodeURIComponent(p.enrollment_id)}`,
                        `ore-asincrone-${asciiName(p.name)}.pdf`)}>
                      PDF
                    </button>
                  )} />
              </div>
            </section>
          )}

          {isFped(data.group) && (
            <FoldedSection title="Prezența la sesiuni" testid="admin-portal-simple-attendance"
              hint="Cine a fost la fiecare sesiune și cât are fiecare din cele 80% cerute de contract.">
              <SimpleAttendance base="/admin/portal" groupId={groupId} />
            </FoldedSection>
          )}
          {!isFped(data.group) && (<>
          <PresenceSection groupId={groupId} onOpen={onOpen} />
          <FoldedSection title="Portofoliile și Anexa 6" testid="admin-portal-portfolios"
            hint="Calendarul evaluării și termenul portofoliului, verificarea structurii, retransmiterile, Anexa 6.">
            <PortfolioBoard base="/admin/portal" groupId={groupId} team />
          </FoldedSection>
          <FoldedSection title="Comisia și Anexele 8 și 9" testid="admin-portal-composition"
            hint="Comisia de evaluare, fișele evaluatorilor, media, cine ajunge la susținere, Anexele 8 și 9.">
            <CompositionBoard groupId={groupId} />
          </FoldedSection>
          <FoldedSection title="Susținerea și Anexele 7, 10, 11 și 12" testid="admin-portal-final"
            hint="Decizia comisiei, prezența la susținere, a doua rundă de punctaj pe aceeași grilă, promovarea.">
            <FinalBoard groupId={groupId} />
          </FoldedSection>
          </>)}

          <section className={`${card} p-5`}>
            <h3 className="text-sm font-semibold text-[#373A45]">Așteaptă feedback</h3>
            {data.todo.length === 0 ? <p className="mt-2 text-sm text-[#6B7280]">Nimic.</p> : (
              <ul className="mt-2 divide-y divide-[#E5E7EB]">
                {data.todo.map((t) => (
                  <li key={`${t.enrollment_id}-${t.activity_id}`}>
                    <button type="button" onClick={() => onOpen(t.enrollment_id)}
                      className="flex w-full items-center gap-3 py-2.5 text-left text-sm hover:text-[#965EA7]">
                      <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{t.name}</span> · {t.title}</span>
                      <span className={`shrink-0 text-xs font-semibold ${t.late ? "text-[#a33a32]" : "text-[#6B7280]"}`}>{waitingFor(t.since)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

        </>
      )}
    </div>
  );
}

/** The live sessions, standing per component and the annexes — folded, since
 *  it is long and not what this view is opened for every time. */
function PresenceSection({ groupId, onOpen }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={`${card} p-5`} data-testid="admin-portal-presence">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#373A45]">Prezența, frecvența și anexele</h3>
          <p className="mt-0.5 text-xs text-[#6B7280]">Sesiunile live de marcat și confirmat, frecvența pe componente, Anexele 4.2, 4.3 și 5.</p>
        </div>
        <button type="button" className={quietBtn} onClick={() => setOpen((v) => !v)} data-testid="admin-portal-presence-toggle">
          {open ? "Ascunde" : "Deschide"}
        </button>
      </div>
      {open && <div className="mt-5"><PresenceBoard base="/admin/portal" groupId={groupId} team onOpenParticipant={onOpen} /></div>}
    </section>
  );
}

function FoldedSection({ title, hint, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <section className={`${card} p-5`} data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#373A45]">{title}</h3>
          <p className="mt-0.5 text-xs text-[#6B7280]">{hint}</p>
        </div>
        <button type="button" className={quietBtn} onClick={() => setOpen((v) => !v)} data-testid={`${testid}-toggle`}>
          {open ? "Ascunde" : "Deschide"}
        </button>
      </div>
      {open && <div className="mt-5">{children}</div>}
    </section>
  );
}

function SheetView({ groupId, enrollmentId, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/portal/groups/${groupId}/participants/${enrollmentId}`).then((r) => setData(r.data))
      .catch((err) => setError(apiErrorMessage(err, "Cursantul nu e în portal.")));
  }, [groupId, enrollmentId]);

  return (
    <div className="grid gap-6">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Înapoi la grupă
      </button>
      {error && <p className="text-sm text-[#c0483f]">{error}</p>}
      {!data && !error && <Loader2 className="mx-auto animate-spin text-[#6B7280]" />}
      {data && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{data.group.name}</p>
              <h2 className="font-display text-2xl font-bold text-[#373A45]">{data.participant.name}</h2>
            </div>
            <PreviewButton target={{ enrollmentId }} label="Vezi portalul ca acest cursant" testid="admin-portal-preview-sheet" />
          </div>
          {data.items.map(({ activity, cell, submission }) => (
            <section key={activity.id} className={`${card} p-5`}>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-[#373A45]">{activity.title}</h3>
                <Chip tone={cell.late_feedback ? "late" : cellOf(cell).tone}>{cellOf(cell).label}</Chip>
              </div>
              {submission.versions.length === 0
                ? <p className="mt-2 text-sm text-[#6B7280]">Nu a predat nimic încă.</p>
                : <SubmissionHistory versions={submission.versions} feedback={submission.feedback} title="Ce a predat"
                    downloadBase="/admin/portal" />}
            </section>
          ))}
          <section className={`${card} p-5`}>
            <h3 className="font-semibold text-[#373A45]">Dosarul personal</h3>
            <p className="mt-0.5 text-xs text-[#6B7280]">Materialele lui de lucru. Formatorul nu le vede; descărcarea se trece în audit.</p>
            {data.dossier.length === 0 ? <p className="mt-2 text-sm text-[#6B7280]">Gol.</p> : (
              <ul className="mt-2 grid gap-1">
                {data.dossier.map((f) => (
                  <li key={f.id}>
                    <button type="button" onClick={() => downloadFile(f.id, (m) => toast.error(m), "/admin/portal")}
                      className="inline-flex items-center gap-1.5 text-sm hover:text-[#965EA7]">
                      <Download size={14} /> {f.name} <span className="text-xs text-[#6B7280]">{formatBytes(f.size)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
